import AIAgentService from "services/aiAgent.service"
import { useMutation, useQuery } from "@tanstack/react-query"
import { hasOngoingProcesses } from "helpers/utils.helper"
import type { TInstructionsData, TInstructionStatus, TBackendChatHistory } from "types/aiAgent.type"

const POLLING_INTERVAL_MS = 5000

export const useGetInstructions = () => {
	return useQuery({
		queryKey: ["getInstructions"],
		queryFn: () => AIAgentService.getInstructions(),
		select: ({ data }) => data,
	})
}

export const useGetQuestions = (drawingId: number) => {
	return useQuery({
		queryKey: ["getQuestions", drawingId],
		queryFn: () => AIAgentService.getQuestions(drawingId),
		select: ({ data }) => data,
		enabled: !!drawingId,
		refetchInterval: (query) => {
			const data = query.state.data?.data
			return hasOngoingProcesses(data) ? POLLING_INTERVAL_MS : false
		},
	})
}

export const useAskCustomQuestion = () => {
	return useMutation({
		mutationKey: ["askCustomQuestion"],
		mutationFn: ({
			drawingId,
			subject,
			messages,
			langsmithTraceId,
			question,
		}: {
			drawingId: number
			subject: string
			langsmithTraceId: string | undefined
			question: string
			messages: TBackendChatHistory[]
		}) => AIAgentService.askCustomQuestion(drawingId, subject, messages, langsmithTraceId, question),
	})
}

export const useAskSpecificQuestion = () => {
	return useMutation({
		mutationKey: ["askSpecificQuestion"],
		mutationFn: ({
			drawingId,
			questionId,
			subject,
			messages,
			userInput,
			type,
		}: {
			drawingId: number
			questionId: number
			subject: string
			messages: TBackendChatHistory[]
			userInput?: {
				[key: string]: string
			}
			type?: string
		}) => AIAgentService.askSpecificQuestion(drawingId, questionId, subject, messages, userInput, type),
	})
}

export const useSendFeedback = () => {
	return useMutation({
		mutationKey: ["sendFeedback"],
		mutationFn: ({
			drawingId,
			langsmithTraceId,
			type,
			feedback,
			drawingUrl,
			manuallySubmittedByUser,
		}: {
			drawingId: number
			langsmithTraceId: string | undefined
			type: "POSITIVE" | "NEGATIVE"
			feedback?: string
			drawingUrl?: string
			manuallySubmittedByUser?: boolean
		}) => AIAgentService.sendFeedback(drawingId, langsmithTraceId, type, feedback, drawingUrl, manuallySubmittedByUser),
	})
}

export const useAddInstruction = () => {
	return useMutation({
		mutationKey: ["addInstruction"],
		mutationFn: (instruction: TInstructionsData) => AIAgentService.addInstruction(instruction),
	})
}

export const useUpdateInstruction = () => {
	return useMutation({
		mutationKey: ["updateInstruction"],
		mutationFn: ({ question, prompt, sections, questionId }: TInstructionsData) =>
			AIAgentService.updateInstruction({ question, prompt, sections, questionId }),
	})
}

export const useDeleteInstruction = () => {
	return useMutation({
		mutationKey: ["deleteInstruction"],
		mutationFn: (questionId: number) => AIAgentService.deleteInstruction(questionId),
	})
}

export const useUpdateInstructionStatus = () => {
	return useMutation({
		mutationKey: ["updateInstructionStatus"],
		mutationFn: ({ questionId, state }: { questionId: number; state: TInstructionStatus }) =>
			AIAgentService.updateInstructionStatus(questionId, state),
	})
}

export const useAddBidRequest = () => {
	return useMutation({
		mutationKey: ["addBidRequest"],
		mutationFn: ({
			drawingId,
			langsmithTraceId,
			llmSelections,
			userConfirmation,
			seriesName,
		}: {
			drawingId: number
			langsmithTraceId: string | undefined
			seriesName: string
			llmSelections: Record<string, string>
			userConfirmation: Record<string, string>
		}) => AIAgentService.addBidRequest(drawingId, langsmithTraceId, llmSelections, userConfirmation, seriesName),
	})
}
